import { API_ENDPOINT } from '@shared/constants/api-endpoints';
import { LanguageCode } from '@shared/constants/language';
import { LanguagePreference } from '@shared/models/UserPreferencesDTO.model';
import { AppActions } from '@shared/store/app/app';
import { selectUserPreferences } from '@shared/store/app/app.selectors';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useFetch } from './useFetch.ts';

const LanguageMapper: Record<LanguageCode, LanguagePreference> = {
  [LanguageCode.EN]: { name: 'English US', value: LanguageCode.EN },
  [LanguageCode.GR]: { name: 'Greek', value: LanguageCode.GR }
};
const useI18n = () => {
  const dispatch = useDispatch();
  const { i18n } = useTranslation();
  const {
    post: postUserPreferences,
    loading: updateLoading,
    error: updateError
  } = useFetch(API_ENDPOINT.USER_PREFERENCES);

  const userLanguage = useSelector(selectUserPreferences).language.value;

  const changeLanguage = async (languageCode: LanguageCode, changeUIOnly?: boolean) => {
    if (changeUIOnly) {
      updateLanguageLocally(languageCode);
    } else {
      await updateUserLanguage(languageCode);
    }
  };

  const updateLanguageLocally = (languageCode: LanguageCode) => {
    i18n.changeLanguage(languageCode);
  };

  const updateUserLanguage = async (languageCode: LanguageCode) => {
    try {
      const payload = { language: LanguageMapper[languageCode] };
      await postUserPreferences({ payload, shouldAbort: false });
      if (languageCode === userLanguage) return;
      dispatch(AppActions.setUserPreferencesLanguage(LanguageMapper[languageCode]));
    } catch (error) {
      console.error('Failed to fetch user preferences:', error);
    }
  };

  useEffect(() => {
    if (userLanguage === i18n.language) return;
    i18n.changeLanguage(userLanguage);
  }, [i18n, userLanguage]);

  return { changeLanguage, updateLoading, updateError, i18nActiveLanguage: i18n.language };
};

export default useI18n;
